import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { createSale, getSales, deleteSale, getStores } from '../api/api';
import { Plus, DollarSign, Calendar, Trash2, Building2 } from 'lucide-react';
import PrivacyBlur, { PrivacyNoticeBanner } from '../components/PrivacyBlur';
import { shouldBlurData, isCashierRole } from '../utils/privacyUtils';

const Sales = () => {
  const { user } = useAuth();
  const [sales, setSales] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState('single'); // 'single' o 'multiple'
  const [formData, setFormData] = useState({
    seller_name: '',
    payment_method: 'Efectivo',
    pos_type: '',  // Visa o Credomatic
    amount: '',
    store_id: ''  // Para admin seleccionar tienda
  });
  // Estado para ventas múltiples
  const [multipleFormData, setMultipleFormData] = useState({
    seller_name: '',
    efectivo: '',
    pos_visa: '',
    pos_credomatic: '',
    recargas: '',
    recaudo: '',
    sobrantes: '',
    transferencia: '',
    store_id: ''  // Para admin seleccionar tienda
  });
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    store_id: '',
    payment_method: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isAdmin = user?.role === 'admin';
  const isAdminOrSupervisor = ['admin', 'manager', 'supervisor', 'admin_assistant'].includes(user?.role);

  // Cargar tiendas para el filtro (solo admin/supervisor/manager)
  useEffect(() => {
    const loadStores = async () => {
      if (isAdminOrSupervisor) {
        try {
          const storesData = await getStores();
          setStores(storesData);
        } catch (error) {
          console.error('Error loading stores:', error);
        }
      }
    };
    loadStores();
  }, [isAdminOrSupervisor]);

  const handleDeleteSale = async (saleId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta venta? Esta acción no se puede deshacer.')) return;
    
    try {
      await deleteSale(saleId);
      setSuccess('Venta eliminada exitosamente');
      loadSales();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al eliminar la venta');
      setTimeout(() => setError(''), 3000);
    }
  };

  const loadSales = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.start_date) params.start_date = new Date(filters.start_date).toISOString();
      if (filters.end_date) {
        const endDate = new Date(filters.end_date);
        endDate.setHours(23, 59, 59);
        params.end_date = endDate.toISOString();
      }
      if (filters.store_id) params.store_id = filters.store_id;
      
      const data = await getSales(params);
      setSales(data);
    } catch (error) {
      console.error('Error loading sales:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadSales();
  }, [loadSales]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    // Validar que si es POS, debe seleccionar Visa o Credomatic
    if (formData.payment_method === 'POS' && !formData.pos_type) {
      setError('Debe seleccionar el tipo de POS (Visa o Credomatic)');
      return;
    }
    
    // Validar que admin seleccione tienda
    if (isAdmin && !formData.store_id) {
      setError('Debe seleccionar una tienda');
      return;
    }
    
    setSubmitting(true);

    try {
      const saleData = {
        seller_name: formData.seller_name,
        payment_method: formData.payment_method,
        amount: parseFloat(formData.amount)
      };
      
      // Solo incluir pos_type si el método es POS
      if (formData.payment_method === 'POS') {
        saleData.pos_type = formData.pos_type;
      }
      
      // Incluir store_id si admin lo seleccionó
      if (isAdmin && formData.store_id) {
        saleData.store_id = formData.store_id;
      }
      
      await createSale(saleData);
      setSuccess('¡Venta registrada exitosamente!');
      setFormData({ seller_name: '', payment_method: 'Efectivo', pos_type: '', amount: '', store_id: '' });
      setShowForm(false);
      loadSales();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al registrar la venta');
    } finally {
      setSubmitting(false);
    }
  };

  // Manejar submit de ventas múltiples
  const handleMultipleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!multipleFormData.seller_name.trim()) {
      setError('Debe ingresar el nombre del vendedor');
      return;
    }
    
    // Validar que admin seleccione tienda
    if (isAdmin && !multipleFormData.store_id) {
      setError('Debe seleccionar una tienda');
      return;
    }
    
    // Verificar que al menos un campo tenga valor
    const hasValue = ['efectivo', 'pos_visa', 'pos_credomatic', 'recargas', 'recaudo', 'sobrantes']
      .some(field => multipleFormData[field] && parseFloat(multipleFormData[field]) > 0);
    
    if (!hasValue) {
      setError('Debe ingresar al menos un monto');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const salesCreated = [];
      const storeId = isAdmin ? multipleFormData.store_id : undefined;
      
      // Crear venta de Efectivo si tiene valor
      if (multipleFormData.efectivo && parseFloat(multipleFormData.efectivo) > 0) {
        await createSale({
          seller_name: multipleFormData.seller_name,
          payment_method: 'Efectivo',
          amount: parseFloat(multipleFormData.efectivo),
          ...(storeId && { store_id: storeId })
        });
        salesCreated.push('Efectivo');
      }
      
      // Crear venta de POS Visa si tiene valor
      if (multipleFormData.pos_visa && parseFloat(multipleFormData.pos_visa) > 0) {
        await createSale({
          seller_name: multipleFormData.seller_name,
          payment_method: 'POS',
          pos_type: 'Visa',
          amount: parseFloat(multipleFormData.pos_visa),
          ...(storeId && { store_id: storeId })
        });
        salesCreated.push('POS Visa');
      }
      
      // Crear venta de POS Credomatic si tiene valor
      if (multipleFormData.pos_credomatic && parseFloat(multipleFormData.pos_credomatic) > 0) {
        await createSale({
          seller_name: multipleFormData.seller_name,
          payment_method: 'POS',
          pos_type: 'Credomatic',
          amount: parseFloat(multipleFormData.pos_credomatic),
          ...(storeId && { store_id: storeId })
        });
        salesCreated.push('POS Credomatic');
      }
      
      // Crear venta de Recargas si tiene valor
      if (multipleFormData.recargas && parseFloat(multipleFormData.recargas) > 0) {
        await createSale({
          seller_name: multipleFormData.seller_name,
          payment_method: 'Recargas',
          amount: parseFloat(multipleFormData.recargas),
          ...(storeId && { store_id: storeId })
        });
        salesCreated.push('Recargas');
      }
      
      // Crear venta de Recaudo si tiene valor
      if (multipleFormData.recaudo && parseFloat(multipleFormData.recaudo) > 0) {
        await createSale({
          seller_name: multipleFormData.seller_name,
          payment_method: 'Recaudo',
          amount: parseFloat(multipleFormData.recaudo),
          ...(storeId && { store_id: storeId })
        });
        salesCreated.push('Recaudo');
      }
      
      // Crear venta de Sobrantes si tiene valor
      if (multipleFormData.sobrantes && parseFloat(multipleFormData.sobrantes) > 0) {
        await createSale({
          seller_name: multipleFormData.seller_name,
          payment_method: 'Sobrantes',
          amount: parseFloat(multipleFormData.sobrantes),
          ...(storeId && { store_id: storeId })
        });
        salesCreated.push('Sobrantes');
      }
      
      // Crear venta de Transferencia si tiene valor
      if (multipleFormData.transferencia && parseFloat(multipleFormData.transferencia) > 0) {
        await createSale({
          seller_name: multipleFormData.seller_name,
          payment_method: 'Transferencia',
          amount: parseFloat(multipleFormData.transferencia),
          ...(storeId && { store_id: storeId })
        });
        salesCreated.push('Transferencia');
      }
      
      setSuccess(`¡${salesCreated.length} venta(s) registrada(s) exitosamente! (${salesCreated.join(', ')})`);
      setMultipleFormData({
        seller_name: '',
        efectivo: '',
        pos_visa: '',
        pos_credomatic: '',
        recargas: '',
        recaudo: '',
        sobrantes: '',
        transferencia: ''
      });
      setShowForm(false);
      loadSales();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al registrar las ventas');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // Asegurar que la fecha se interprete como UTC
    let utcDateString = dateString;
    if (!dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
      utcDateString = dateString + 'Z';
    }
    const date = new Date(utcDateString);
    return date.toLocaleString('es-GT', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="space-y-8">
      {/* Header futurista - Responsivo */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent" data-testid="sales-title">
            Registro de Ventas
          </h1>
          <p className="text-gray-600 mt-1 sm:mt-2 text-sm sm:text-base">Gestiona todas las ventas de tu tienda</p>
        </div>
        {user?.role !== 'admin_assistant' && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2 font-semibold text-sm sm:text-base"
            data-testid="add-sale-button"
          >
            <Plus size={20} />
            <span>Nueva Venta</span>
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-700 px-6 py-4 rounded-xl flex items-center space-x-3" data-testid="sales-error">
          <span className="text-2xl">⚠️</span>
          <span className="font-medium">{error}</span>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border-2 border-green-200 text-green-700 px-6 py-4 rounded-xl flex items-center space-x-3 animate-pulse" data-testid="sales-success">
          <span className="text-2xl">✅</span>
          <span className="font-medium">{success}</span>
        </div>
      )}

      {/* Filtros con diseño moderno - Responsivo */}
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-4 sm:p-6 border border-gray-100">
        <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
          <Calendar className="text-blue-600" size={20} />
          <span>Filtros de Búsqueda</span>
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
          {/* Filtro de Tienda - Solo para Admin/Supervisor/Manager */}
          {isAdminOrSupervisor && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-1">
                <Building2 size={16} className="text-blue-600" />
                <span>Tienda</span>
              </label>
              <select
                value={filters.store_id}
                onChange={(e) => setFilters({ ...filters, store_id: e.target.value })}
                className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                data-testid="store-filter"
              >
                <option value="">Todas las tiendas</option>
                {stores.map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Método de Pago
            </label>
            <select
              value={filters.payment_method}
              onChange={(e) => setFilters({ ...filters, payment_method: e.target.value })}
              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
              data-testid="payment-method-filter"
            >
              <option value="">Todos los métodos</option>
              <option value="Efectivo">Efectivo</option>
              <option value="POS">POS (Tarjeta)</option>
              <option value="Recargas">Recargas</option>
              <option value="Recaudo">Recaudo</option>
              <option value="Sobrantes">Sobrantes</option>
              <option value="Transferencia">Transferencia</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fecha Inicio
            </label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fecha Fin
            </label>
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
            />
          </div>
          <div className="flex items-end col-span-1 sm:col-span-2 lg:col-span-1">
            <button
              onClick={() => setFilters({ start_date: '', end_date: '', store_id: '', payment_method: '' })}
              className="w-full px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors text-sm sm:text-base"
            >
              Limpiar Filtros
            </button>
          </div>
        </div>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow-md p-6">
          {/* Selector de modo */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold">Registrar Nueva Venta</h2>
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                type="button"
                onClick={() => setFormMode('single')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  formMode === 'single' 
                    ? 'bg-blue-600 text-white shadow' 
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                Venta Simple
              </button>
              <button
                type="button"
                onClick={() => setFormMode('multiple')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  formMode === 'multiple' 
                    ? 'bg-green-600 text-white shadow' 
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                Ventas Múltiples
              </button>
            </div>
          </div>

          {/* Formulario Simple */}
          {formMode === 'single' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Selector de tienda para admin */}
              {isAdmin && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                  <label className="block text-sm font-medium text-amber-800 mb-2">
                    <Building2 className="inline mr-2" size={16} />
                    Seleccionar Tienda (Admin)
                  </label>
                  <select
                    value={formData.store_id}
                    onChange={(e) => setFormData({ ...formData, store_id: e.target.value })}
                    className="w-full px-4 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 bg-white"
                    required
                  >
                    <option value="">-- Seleccione una tienda --</option>
                    {stores.map((store) => (
                      <option key={store.id} value={store.id}>
                        {store.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre del Vendedor
                  </label>
                  <input
                    type="text"
                    value={formData.seller_name}
                    onChange={(e) => setFormData({ ...formData, seller_name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Nombre del vendedor"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Forma de Pago
                  </label>
                  <select
                    value={formData.payment_method}
                    onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Efectivo">Efectivo</option>
                    <option value="POS">POS/Tarjeta</option>
                    <option value="Recargas">Recargas</option>
                    <option value="Recaudo">Recaudo</option>
                    <option value="Sobrantes">Sobrantes</option>
                    <option value="Transferencia">Transferencia</option>
                  </select>
                </div>
                {formData.payment_method === 'POS' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      💳 Tipo de POS
                    </label>
                    <select
                      value={formData.pos_type}
                      onChange={(e) => setFormData({ ...formData, pos_type: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Seleccionar...</option>
                      <option value="Visa">Visa</option>
                      <option value="Credomatic">Credomatic</option>
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Monto
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>
              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400"
                >
                  {submitting ? 'Registrando...' : 'Registrar Venta'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          )}

          {/* Formulario de Ventas Múltiples */}
          {formMode === 'multiple' && (
            <form onSubmit={handleMultipleSubmit} className="space-y-4">
              {/* Selector de tienda para admin */}
              {isAdmin && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                  <label className="block text-sm font-medium text-amber-800 mb-2">
                    <Building2 className="inline mr-2" size={16} />
                    Seleccionar Tienda (Admin)
                  </label>
                  <select
                    value={multipleFormData.store_id}
                    onChange={(e) => setMultipleFormData({ ...multipleFormData, store_id: e.target.value })}
                    className="w-full px-4 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 bg-white"
                    required
                  >
                    <option value="">-- Seleccione una tienda --</option>
                    {stores.map((store) => (
                      <option key={store.id} value={store.id}>
                        {store.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="text-green-800 text-sm">
                  💡 <strong>Ingrese todos los montos aplicables.</strong> Se crearán ventas separadas para cada tipo de pago con valor mayor a 0.
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del Vendedor *
                </label>
                <input
                  type="text"
                  value={multipleFormData.seller_name}
                  onChange={(e) => setMultipleFormData({ ...multipleFormData, seller_name: e.target.value })}
                  className="w-full md:w-1/2 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="Nombre del vendedor"
                  required
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <label className="block text-sm font-bold text-green-800 mb-2">
                    💵 Efectivo
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={multipleFormData.efectivo}
                    onChange={(e) => setMultipleFormData({ ...multipleFormData, efectivo: e.target.value })}
                    className="w-full px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="0.00"
                  />
                </div>
                
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <label className="block text-sm font-bold text-blue-800 mb-2">
                    💳 POS Visa
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={multipleFormData.pos_visa}
                    onChange={(e) => setMultipleFormData({ ...multipleFormData, pos_visa: e.target.value })}
                    className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                  />
                </div>
                
                <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                  <label className="block text-sm font-bold text-indigo-800 mb-2">
                    💳 POS Credomatic
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={multipleFormData.pos_credomatic}
                    onChange={(e) => setMultipleFormData({ ...multipleFormData, pos_credomatic: e.target.value })}
                    className="w-full px-3 py-2 border border-indigo-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="0.00"
                  />
                </div>
                
                <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                  <label className="block text-sm font-bold text-amber-800 mb-2">
                    📱 Recargas
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={multipleFormData.recargas}
                    onChange={(e) => setMultipleFormData({ ...multipleFormData, recargas: e.target.value })}
                    className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    placeholder="0.00"
                  />
                </div>
                
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <label className="block text-sm font-bold text-purple-800 mb-2">
                    📋 Recaudo
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={multipleFormData.recaudo}
                    onChange={(e) => setMultipleFormData({ ...multipleFormData, recaudo: e.target.value })}
                    className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="0.00"
                  />
                </div>
                
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <label className="block text-sm font-bold text-red-800 mb-2">
                    💰 Sobrantes
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={multipleFormData.sobrantes}
                    onChange={(e) => setMultipleFormData({ ...multipleFormData, sobrantes: e.target.value })}
                    className="w-full px-3 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    placeholder="0.00"
                  />
                </div>
                
                <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
                  <label className="block text-sm font-bold text-teal-800 mb-2">
                    🏦 Transferencia
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={multipleFormData.transferencia}
                    onChange={(e) => setMultipleFormData({ ...multipleFormData, transferencia: e.target.value })}
                    className="w-full px-3 py-2 border border-teal-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:shadow-lg transition-all disabled:bg-gray-400 font-semibold"
                >
                  {submitting ? 'Registrando...' : '✓ Registrar Todas las Ventas'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Privacy Notice Banner for Cashiers */}
      <PrivacyNoticeBanner show={isCashierRole(user)} />

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold">Historial de Ventas</h2>
        </div>
        
        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block w-12 h-12 border-4 border-blue-500/30 border-t-cyan-600 rounded-full animate-spin"></div>
            <p className="text-gray-600 mt-4">Cargando ventas...</p>
          </div>
        ) : sales.length === 0 ? (
          <div className="p-12 text-center">
            <div className="bg-gray-100 rounded-full w-24 h-24 mx-auto flex items-center justify-center mb-4">
              <DollarSign className="text-gray-400" size={48} />
            </div>
            <p className="text-gray-600 font-medium">No hay ventas registradas</p>
          </div>
        ) : (
          (() => {
            // Filter sales by payment method if selected
            const filteredSales = filters.payment_method 
              ? sales.filter(s => s.payment_method === filters.payment_method)
              : sales;
            
            // Calculate totals by payment method for summary
            const salesByMethod = sales.reduce((acc, sale) => {
              const method = sale.payment_method || 'Otro';
              acc[method] = (acc[method] || 0) + sale.amount;
              return acc;
            }, {});

            return (
              <>
                {/* Summary by payment method - Responsivo */}
                <div className="p-3 sm:p-4 bg-slate-50 border-b">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 flex-wrap">
                    <span className="text-sm font-semibold text-slate-700">Resumen:</span>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(salesByMethod).map(([method, total]) => (
                        <span 
                          key={method} 
                          className={`px-2 py-1 text-xs rounded-lg cursor-pointer transition-all ${
                            filters.payment_method === method 
                              ? 'bg-blue-600 text-white' 
                              : 'bg-white border text-slate-600 hover:bg-blue-50'
                          }`}
                          onClick={() => setFilters({ ...filters, payment_method: filters.payment_method === method ? '' : method })}
                        >
                          {method}: Q{total.toLocaleString('es-GT', { minimumFractionDigits: 2 })}
                        </span>
                      ))}
                    </div>
                    <span className="sm:ml-auto px-2 py-1 text-xs font-bold bg-emerald-100 text-emerald-800 rounded-lg">
                      Total: Q{sales.reduce((sum, s) => sum + s.amount, 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
                
                {/* Vista de Tabla para Desktop */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="bg-gradient-to-r from-slate-700 to-blue-700 text-white">
                        <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wide rounded-tl-xl">Fecha/Hora</th>
                        {isAdminOrSupervisor && <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wide">Tienda</th>}
                        <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wide">Vendedor</th>
                        <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wide">Forma de Pago</th>
                        <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wide">Monto</th>
                        <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wide">Descripción</th>
                        <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wide">Registrado Por</th>
                        {isAdmin && <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wide rounded-tr-xl">Acciones</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredSales.map((sale, index) => {
                        const isBlurred = shouldBlurData(user, sale.timestamp);
                        return (
                        <tr key={sale.id} className={`hover:bg-blue-50/50 transition-all duration-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} ${isBlurred ? 'opacity-75' : ''}`}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                            <PrivacyBlur isBlurred={isBlurred}>
                              {formatDate(sale.timestamp)}
                            </PrivacyBlur>
                          </td>
                          {isAdminOrSupervisor && (
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-lg">
                                {sale.store_name || 'N/A'}
                              </span>
                            </td>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
                            <PrivacyBlur isBlurred={isBlurred}>
                              {sale.seller_name}
                            </PrivacyBlur>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <PrivacyBlur isBlurred={isBlurred}>
                              <span className={`px-3 py-1.5 inline-flex text-xs font-bold rounded-lg shadow-sm ${
                                sale.payment_method === 'Efectivo' ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white' :
                                sale.payment_method === 'POS' ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white' :
                                sale.payment_method === 'Recargas' ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white' :
                                sale.payment_method === 'Recaudo' ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white' :
                                sale.payment_method === 'Sobrantes' ? 'bg-gradient-to-r from-red-500 to-red-600 text-white' :
                                sale.payment_method === 'Transferencia' ? 'bg-gradient-to-r from-teal-500 to-teal-600 text-white' :
                                'bg-gray-400 text-white'
                              }`}>
                                {sale.payment_method}
                                {sale.pos_type && ` (${sale.pos_type})`}
                              </span>
                            </PrivacyBlur>
                            {sale.is_petty_cash_transfer && (
                              <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">
                                💰 Sencillo
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-base font-bold text-emerald-600">
                            <PrivacyBlur isBlurred={isBlurred}>
                              {sale.currency || 'Q'}{sale.amount.toFixed(2)}
                            </PrivacyBlur>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate" title={sale.description || ''}>
                            <PrivacyBlur isBlurred={isBlurred}>
                              {sale.description || '-'}
                            </PrivacyBlur>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            <PrivacyBlur isBlurred={isBlurred}>
                              {sale.created_by_name}
                            </PrivacyBlur>
                          </td>
                          {isAdmin && (
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button
                                onClick={() => handleDeleteSale(sale.id)}
                                className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                title="Eliminar venta"
                              >
                                <Trash2 size={18} />
                              </button>
                            </td>
                          )}
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Vista de Tarjetas para Móvil */}
                <div className="block md:hidden space-y-3 p-4">
                  {filteredSales.map((sale) => {
                    const isBlurred = shouldBlurData(user, sale.timestamp);
                    return (
                      <div 
                        key={sale.id} 
                        className={`bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow ${isBlurred ? 'opacity-75' : ''}`}
                        data-testid={`sale-card-${sale.id}`}
                      >
                        {/* Header: Monto y Método de Pago */}
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <PrivacyBlur isBlurred={isBlurred}>
                              <span className={`px-3 py-1.5 inline-flex text-xs font-bold rounded-lg shadow-sm ${
                                sale.payment_method === 'Efectivo' ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white' :
                                sale.payment_method === 'POS' ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white' :
                                sale.payment_method === 'Recargas' ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white' :
                                sale.payment_method === 'Recaudo' ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white' :
                                sale.payment_method === 'Sobrantes' ? 'bg-gradient-to-r from-red-500 to-red-600 text-white' :
                                sale.payment_method === 'Transferencia' ? 'bg-gradient-to-r from-teal-500 to-teal-600 text-white' :
                                'bg-gray-400 text-white'
                              }`}>
                                {sale.payment_method}
                                {sale.pos_type && ` (${sale.pos_type})`}
                              </span>
                            </PrivacyBlur>
                            {sale.is_petty_cash_transfer && (
                              <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">
                                💰 Sencillo
                              </span>
                            )}
                          </div>
                          <PrivacyBlur isBlurred={isBlurred}>
                            <span className="text-xl font-bold text-emerald-600">
                              {sale.currency || 'Q'}{sale.amount.toFixed(2)}
                            </span>
                          </PrivacyBlur>
                        </div>

                        {/* Información Principal */}
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500">Vendedor:</span>
                            <PrivacyBlur isBlurred={isBlurred}>
                              <span className="font-semibold text-gray-900">{sale.seller_name}</span>
                            </PrivacyBlur>
                          </div>
                          
                          {isAdminOrSupervisor && (
                            <div className="flex items-center justify-between">
                              <span className="text-gray-500">Tienda:</span>
                              <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-lg">
                                {sale.store_name || 'N/A'}
                              </span>
                            </div>
                          )}
                          
                          {sale.description && (
                            <div className="flex items-center justify-between">
                              <span className="text-gray-500">Descripción:</span>
                              <PrivacyBlur isBlurred={isBlurred}>
                                <span className="text-gray-700 truncate max-w-[150px]">{sale.description}</span>
                              </PrivacyBlur>
                            </div>
                          )}
                          
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500">Registrado por:</span>
                            <PrivacyBlur isBlurred={isBlurred}>
                              <span className="text-gray-600">{sale.created_by_name}</span>
                            </PrivacyBlur>
                          </div>
                        </div>

                        {/* Footer: Fecha y Acciones */}
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                          <PrivacyBlur isBlurred={isBlurred}>
                            <span className="text-xs text-gray-500">
                              {formatDate(sale.timestamp)}
                            </span>
                          </PrivacyBlur>
                          {isAdmin && (
                            <button
                              onClick={() => handleDeleteSale(sale.id)}
                              className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                              title="Eliminar venta"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
          </>
            );
          })()
        )}
      </div>
    </div>
  );
};

export default Sales;